// PRE RMA CSB — Moteur de validation d'une consultation.
// Reproduit exactement les regles des colonnes Z/AA/AB/AC/AD de Saisie_Unique
// (Phase1 3.4, regles numerotees 1 a 31 dans docs/PHASE1_ANALYSE.md).
// Une seule difference volontaire: aucune (les 3 corrections ne touchent
// pas au moteur de validation, seulement a RMA Partie1-ligne41 et au
// Tableau de suivi).

import { parseAge } from './age.ts';
import { DIAGNOSTICS_REF, DIAGNOSTICS_PALUDISME, TDR_POSITIFS, TRAITEMENT_AUTORISE, RULES } from './constants.ts';
import type { Genre, TdrResultat } from './constants.ts';

export interface ConsultationInput {
  numeroRceBrut: string;        // "1" | "2, 3" | "CRENAS" | ""
  casFievre: 'Oui' | 'Non' | null;
  tdrResultat: TdrResultat | null;
  ageBrut: string | null;       // ex: "12 mois"
  genre: Genre | null;
  diagnostics: string[];        // codes DIAGNOSTICS_REF, 0 a 4 elements, ordre F,G,H,I
  typeTraumatisme: string | null; // code, ou null/'AUCUN'
  traitementPalu: string | null;  // libelle exact (voir constants.TRAITEMENT_AUTORISE)
  indicateurCrenas: string | null; // libelle contenant potentiellement plusieurs valeurs, ex "MAM P/T, MAM PB"
  admissionCrenas: string | null;  // libelle exact
  /** Date de consultation AAAA-MM-JJ (obligatoire pour toute nouvelle saisie ; absente sur les anciennes lignes). */
  dateConsultation?: string | null;
}

export interface ValidationResult {
  ok: boolean;
  erreurs: string[];
  ageJours: number | null;
}

/** Format N RCE valide (colonne X): nombre(s) separes par virgule, "CRENAS", ou vide. */
function rceFormatValide(brut: string): boolean {
  const t = brut.trim();
  if (t === '') return true;
  if (t.toUpperCase() === 'CRENAS') return true;
  const compact = t.replace(/\s+/g, '');
  if (compact === '' || compact.startsWith(',') || compact.endsWith(',') || compact.includes(',,')) {
    return false;
  }
  return compact.split(',').every((part) => part !== '' && !Number.isNaN(Number(part)));
}

/** Nombre de codes N RCE (colonne T): 0 si "CRENAS", sinon nb de valeurs separees par virgule. */
function rceCount(brut: string): number {
  const t = brut.trim();
  if (t === '' || t.toUpperCase() === 'CRENAS') return 0;
  return t.split(',').filter((p) => p.trim() !== '').length;
}

function contient(champ: string | null, valeur: string): boolean {
  return !!champ && champ.includes(valeur);
}

const txtOuNull = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const t = String(v).replace(/[\u00A0\u200B-\u200D\uFEFF]/g, ' ').trim();
  return t === '' ? null : t;
};

/**
 * Met une saisie (tapée, collée, importée, relue d'IndexedDB ou reçue d'un client ancien) sous la forme exacte attendue par le moteur :
 * champs absents → valeurs vides, texte « trimé », genre en majuscule, fièvre en Oui/Non, diagnostics vides retirés.
 * Idempotente. Ne devine jamais une valeur : une valeur inconnue reste telle quelle et sera refusée par la validation.
 */
export function normaliserConsultationInput(brut: Partial<Record<keyof ConsultationInput, unknown>> | null | undefined): ConsultationInput {
  const r = (brut ?? {}) as Record<string, unknown>;
  const g = txtOuNull(r.genre)?.toUpperCase() ?? null;
  const f = txtOuNull(r.casFievre)?.toLowerCase() ?? null;
  const trauma = txtOuNull(r.typeTraumatisme);
  return {
    numeroRceBrut: txtOuNull(r.numeroRceBrut) ?? '',
    casFievre: (f === 'oui' ? 'Oui' : f === 'non' ? 'Non' : null) as ConsultationInput['casFievre'],
    tdrResultat: txtOuNull(r.tdrResultat) as ConsultationInput['tdrResultat'],
    ageBrut: txtOuNull(r.ageBrut),
    genre: g as ConsultationInput['genre'],
    diagnostics: Array.isArray(r.diagnostics) ? r.diagnostics.map(txtOuNull).filter((d): d is string => d !== null) : [], // doublons conservés : la règle 9 doit les voir
    typeTraumatisme: trauma && trauma.toUpperCase() === 'AUCUN' ? null : trauma,
    traitementPalu: txtOuNull(r.traitementPalu),
    indicateurCrenas: txtOuNull(r.indicateurCrenas),
    admissionCrenas: txtOuNull(r.admissionCrenas),
    // La date n'est ajoutée que si elle existe : les anciennes saisies gardent exactement leur forme d'origine.
    ...(txtOuNull(r.dateConsultation) ? { dateConsultation: txtOuNull(r.dateConsultation) } : {}),
  };
}

export function validerConsultation(brut: ConsultationInput): ValidationResult {
  const input = normaliserConsultationInput(brut);
  const erreurs: string[] = [];
  const rceVide = input.numeroRceBrut.trim() === '';
  const estCrenas = input.numeroRceBrut.trim().toUpperCase() === 'CRENAS';

  // --- Age ---
  const ageParse = input.ageBrut ? parseAge(input.ageBrut) : { ok: true as const };
  const ageJours = ageParse.ok && 'ageJours' in ageParse ? ageParse.ageJours! : null;
  if (input.ageBrut && !ageParse.ok) erreurs.push('Format Âge invalide');

  // --- N RCE ---
  if (!rceFormatValide(input.numeroRceBrut)) erreurs.push('Format N° RCE invalide');

  // --- Obligatoires (regles 3-6) ---
  if (!rceVide && !estCrenas && !input.ageBrut) erreurs.push('Âge obligatoire');
  if (!rceVide && !estCrenas && !input.genre) erreurs.push('Genre obligatoire');
  if (estCrenas && !input.ageBrut) erreurs.push('Âge obligatoire');
  if (estCrenas && !input.genre) erreurs.push('Genre obligatoire');

  // --- Rien ne doit être saisi « dans le vide » : sans N° RCE, la ligne est ignorée par le RMA (règle du classeur d'origine). ---
  if (rceVide && (input.diagnostics.length > 0 || input.typeTraumatisme)) {
    erreurs.push('N° RCE obligatoire : un diagnostic ou un traumatisme sans N° RCE ne serait compté nulle part dans le RMA');
  }

  // --- CRENAS + comptage diagnostics/traumatismes vs N RCE (regles 7-8) ---
  if (estCrenas && !input.admissionCrenas) erreurs.push('Admission CRENAS obligatoire');
  if (!rceVide && !estCrenas) {
    const nbDiag = new Set(input.diagnostics.filter(Boolean)).size;
    const traumaCompte =
      input.typeTraumatisme && input.typeTraumatisme !== 'AUCUN' ? 1 : 0;
    const attendu = rceCount(input.numeroRceBrut);
    if (nbDiag + traumaCompte !== attendu) {
      erreurs.push(
        `Le nombre de diagnostics + traumatismes doit être exactement égal au nombre de codes N° RCE (${attendu})`
      );
    }
  }

  // --- Diagnostic en double (regle 9) ---
  const diagsRemplis = input.diagnostics.filter(Boolean);
  if (new Set(diagsRemplis).size !== diagsRemplis.length) erreurs.push('Diagnostic en double');

  // --- TDR / Fievre (regles 10-11) ---
  if (input.casFievre === 'Oui' && !input.tdrResultat) erreurs.push('TDR obligatoire (Fièvre = Oui)');
  if (input.tdrResultat && input.casFievre !== 'Oui') erreurs.push('TDR incompatible avec Fièvre = Non');

  // --- Paludisme (regles 12-19) ---
  const diagPaluPresent = diagsRemplis.some((d) => (DIAGNOSTICS_PALUDISME as readonly string[]).includes(d));
  if (diagPaluPresent && !input.traitementPalu) erreurs.push('Traitement Palu manquant');
  if (input.traitementPalu && !diagPaluPresent) erreurs.push('Traitement Palu sans diagnostic Paludisme');

  const tdrPositif = input.tdrResultat ? TDR_POSITIFS.includes(input.tdrResultat) : false;
  if (diagPaluPresent && !tdrPositif) erreurs.push('Diagnostic Paludisme incompatible avec le résultat TDR');
  if (tdrPositif && !diagPaluPresent) erreurs.push('TDR positif sans diagnostic Paludisme correspondant');

  if (diagsRemplis.includes('PALUDISME_FE') && input.genre !== 'F') {
    erreurs.push('Paludisme FE incompatible avec le sexe');
  }
  for (const diag of DIAGNOSTICS_PALUDISME) {
    if (diagsRemplis.includes(diag) && input.traitementPalu) {
      const autorises = TRAITEMENT_AUTORISE[diag];
      if (!autorises.includes(input.traitementPalu)) {
        erreurs.push(`${DIAGNOSTICS_REF.find((d) => d.code === diag)!.libelle} : traitement autorisé = ${autorises.join(' ou ')}`);
      }
    }
  }

  // --- Paludisme FE age/TDR + MAS/MAM CRENAS (regles 20-25) ---
  if (diagsRemplis.includes('PALUDISME_FE') && (ageJours === null || ageJours < RULES.age.seuils.cinqAns)) {
    erreurs.push("Paludisme FE incompatible avec l'âge");
  }
  if (diagsRemplis.includes('PALUDISME_FE') && !tdrPositif) {
    erreurs.push('Paludisme FE incompatible avec le résultat TDR');
  }
  const indicMasOk = RULES.crenas.indicateursMas.some((mot) => contient(input.indicateurCrenas, mot));
  if (diagsRemplis.includes('MAS') && !indicMasOk) erreurs.push('MAS sans indicateur CRENAS compatible');

  const indicMamOk = RULES.crenas.indicateursMam.some((mot) => contient(input.indicateurCrenas, mot));
  if (diagsRemplis.includes('MAM') && !indicMamOk) erreurs.push('MAM sans indicateur CRENAS compatible');

  if (indicMasOk && !input.admissionCrenas) erreurs.push('Admission CRENAS manquante');

  if (diagsRemplis.includes('MAS') && input.admissionCrenas) {
    const ok =
      (input.genre === 'M' && RULES.crenas.admissionsParGenre.M.includes(input.admissionCrenas)) ||
      (input.genre === 'F' && RULES.crenas.admissionsParGenre.F.includes(input.admissionCrenas));
    if (!ok) erreurs.push('Admission CRENAS incompatible avec le Genre');
  }

  // --- Regles d'age par diagnostic (regles 26-31, table DIAGNOSTICS_REF) ---
  for (const code of diagsRemplis) {
    const ref = DIAGNOSTICS_REF.find((d) => d.code === code);
    if (!ref) continue;
    if (ref.ageMaxJours !== null && (ageJours === null || ageJours > ref.ageMaxJours)) {
      erreurs.push(`${ref.libelle} incompatible avec l'âge : autorisé uniquement de 0 à ${Math.round(ref.ageMaxJours / 30)} mois`);
    }
    if (ref.ageMinJours !== null && (ageJours === null || ageJours < ref.ageMinJours)) {
      erreurs.push(`${ref.libelle} incompatible avec l'âge : autorisé uniquement à partir de ${Math.round(ref.ageMinJours / 365)} ans`);
    }
    if (ref.genreRequis && input.genre !== ref.genreRequis) {
      erreurs.push(`${ref.libelle} incompatible avec le sexe`);
    }
  }

  return { ok: erreurs.length === 0, erreurs, ageJours };
}
